"use client"

import { useMemo } from "react"
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { Box, Flex, Text, Image, IconButton, Tooltip, Badge } from "@chakra-ui/react"
import { FaRegUser, FaSignOutAlt, FaMapMarkedAlt, FaTable } from "react-icons/fa"

import RetailerNavigation from "../RetailerNavigation"
import OrgToggle from "../OrgToggle"
import useRequestActivity from "../../hooks/useRequestActivity"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { usePageAccess } from "../../wrappers/PageAccessContext"
import { buildLogoCandidates, nextLogoCandidate, BLANK_LOGO } from "../../tools/supplierLogo"
import { neutral, brand, type, radius, layout, elevation, motion } from "../../theme/tokens"

// Titles live with the shell so no page has to render its own — previously
// Sales had no title at all while Strike Rate, Ranging and Margin each styled
// their own differently.
const PAGE_TITLES = {
  "/sales": "Sales",
  "/insights": "Insights",
  "/strike": "Strike Rate",
  "/ranging": "Ranging",
  "/margin": "Margin",
  "/orders": "Orders",
  "/imports": "Imports",
  "/users": "Users",
  "/articles": "Articles",
  "/stores": "Stores",
  "/loginhistory": "Login History",
  "/edi": "EDI",
  "/alerts": "Stock Alerts",
  "/delete": "Delete Data",
  "/pnp-ftp": "PnP FTP",
  "/validation": "Validation",
  "/data-status": "Data Status",
  "/batch-reports": "Batch Reports",
  "/myProfile": "My Profile",
  "/addUser": "Add User",
  "/supplierLogos": "Supplier Logos",
  "/organisations": "Organisations",
  "/org-reports": "Batch Reports",
  "/editPageAccess": "Page Access",
  "/editSupplierAccess": "Supplier Access",
  "/editRetailerAccess": "Retailer Access",
}

const resolveTitle = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname]
  const base = `/${pathname.split("/")[1] || ""}`
  return PAGE_TITLES[base] || "SpearBI"
}

/**
 * The command bar.
 *
 * One instrument that answers "what am I looking at?" — page, supplier,
 * retailer channel — and stays pinned while you scroll. It replaces three
 * disconnected pieces of floating chrome: a retailer strip fixed at
 * `left: 200px`, a supplier logo floating over the page in a circle at
 * `z-index: 3000`, and a "Trial Period" label floating beside it.
 */
const TopBar = ({ onSignOut, trialyn, showChannels }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { selectedSupplier, selectedRetailer, retailers } = useSupplierRetailer()
  const { user_page_access } = usePageAccess()
  const busy = useRequestActivity()

  // Dashboard | Map switch — Sales-page-scoped, and only for accounts holding
  // the MapView page grant (docs/michael/TASK-map-view.md §11: no grant, no
  // hint the feature exists). Lives here in the shell so the Sales page itself
  // gives up no content space.
  const onSales = location.pathname === "/sales"
  const canMap = onSales && user_page_access.some((p) => p.value === "MapView")
  const mapActive = canMap && searchParams.get("view") === "map"
  const setView = (map) => navigate(map ? "/sales?view=map" : "/sales")

  const title = resolveTitle(location.pathname)
  const logoCandidates = useMemo(() => buildLogoCandidates(selectedSupplier), [selectedSupplier])
  const retailerLabel = retailers.find((r) => r.value === selectedRetailer)?.text || selectedRetailer

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={900}
      bg="rgba(255,255,255,0.86)"
      backdropFilter="blur(12px)"
      boxShadow={elevation.sticky}
    >
      {/* Identity row */}
      <Flex align="center" h={layout.topBarHeight} px={layout.contentGutter} gap={3}>
        {selectedSupplier && (
          <Image
            // Remounting per supplier matters: without the key the browser keeps
            // painting the previous logo until the new one decodes.
            key={selectedSupplier}
            src={logoCandidates[0] || BLANK_LOGO}
            onError={({ currentTarget }) => nextLogoCandidate(currentTarget, logoCandidates)}
            alt=""
            boxSize="28px"
            borderRadius={radius.sm}
            objectFit="contain"
            border="1px solid"
            borderColor={neutral[200]}
            bg={neutral[0]}
            flexShrink={0}
          />
        )}

        <Flex align="baseline" gap={2.5} minW={0}>
          <Text
            as="h1"
            fontSize={type.size.title}
            fontWeight={type.weight.semibold}
            letterSpacing={type.tracking.title}
            color={neutral[900]}
            lineHeight={1.2}
            whiteSpace="nowrap"
          >
            {title}
          </Text>

          {selectedSupplier && (
            <Text
              fontSize={type.size.small}
              color={neutral[500]}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {selectedSupplier}
              {retailerLabel ? ` · ${retailerLabel}` : ""}
            </Text>
          )}
        </Flex>

        {trialyn && (
          <Badge bg={brand[50]} color={brand[600]} border="1px solid" borderColor={brand[200]}>
            Trial
          </Badge>
        )}

        {canMap && (
          <Flex
            ml={2}
            borderRadius={radius.sm}
            border="1px solid"
            borderColor={neutral[200]}
            overflow="hidden"
            flexShrink={0}
            role="group"
            aria-label="Sales view"
          >
            {[
              { map: false, label: "Dashboard", icon: FaTable },
              { map: true, label: "Map", icon: FaMapMarkedAlt },
            ].map(({ map, label, icon: Icon }) => {
              const active = map === mapActive
              return (
                <Flex
                  key={label}
                  as="button"
                  onClick={() => setView(map)}
                  align="center"
                  gap={1.5}
                  px={3}
                  py={1}
                  fontSize={type.size.small}
                  fontWeight={type.weight.semibold}
                  bg={active ? brand[500] : neutral[0]}
                  color={active ? neutral[0] : neutral[600]}
                  _hover={active ? {} : { bg: neutral[100] }}
                  transition={`background ${motion.fast}`}
                  aria-pressed={active}
                >
                  <Icon size={11} />
                  {label}
                </Flex>
              )
            })}
          </Flex>
        )}

        <Box flex="1" />

        <OrgToggle />

        <Tooltip label="My profile" placement="bottom" openDelay={300}>
          <IconButton
            as={NavLink}
            to="/myProfile"
            aria-label="My profile"
            icon={<FaRegUser />}
            variant="ghost"
            size="sm"
            fontSize="14px"
          />
        </Tooltip>

        <Tooltip label="Sign out" placement="bottom-end" openDelay={300}>
          <IconButton
            aria-label="Sign out"
            icon={<FaSignOutAlt />}
            onClick={onSignOut}
            variant="ghost"
            size="sm"
            fontSize="14px"
            _hover={{ bg: "signal.downSoft", color: "signal.down" }}
          />
        </Tooltip>
      </Flex>

      {/* Loading rail. The sweep animates `transform` only, so it is driven by
          the compositor and keeps moving while the main thread is blocked
          rendering a 20,000-cell grid — which is exactly when you most need to
          know the app is working rather than hung. */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        h="2px"
        overflow="hidden"
        pointerEvents="none"
        opacity={busy ? 1 : 0}
        transition={`opacity ${motion.fast}`}
        aria-hidden={!busy}
      >
        <Box
          h="100%"
          w="40%"
          bg={brand[500]}
          borderRadius="2px"
          sx={{
            willChange: "transform",
            animation: busy ? "spearSweep 1.1s ease-in-out infinite" : "none",
            "@keyframes spearSweep": {
              "0%": { transform: "translateX(-105%)" },
              "100%": { transform: "translateX(355%)" },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none", w: "100%", opacity: 0.5 },
          }}
        />
      </Box>

      {/* Channel rail */}
      {showChannels && (
        <Box
          h={layout.channelRailHeight}
          px={layout.contentGutter}
          borderTop="1px solid"
          borderColor={neutral[200]}
          transition={`opacity ${motion.base}`}
        >
          <RetailerNavigation />
        </Box>
      )}
    </Box>
  )
}

export default TopBar
